/**
 * 剧本固定排期表（双模式剧本池 · 固定排期模式用）
 *
 * 数据来源：docs/剧本固定排期-18年.md（72 季全量表）。改排期先改那份文档并跑完其 §四 校验，再同步本文件；本文件不手工加减剧本。
 * 方案级校验/出表工具（`_probe_schedule_bands.mjs` + `_refill_placement.mjs`）已于 2026-10-04 清理，git 可查；日常对账跑哨兵 `web/_check_schedule_sync.mjs`，改表须先按 docs/剧本固定排期-18年.md「填写规则」出方案再落表。
 *
 * 2026-10-04 重排（可达带规则 v2）：池 150 本 = 142 主槽 + 8 个第 3 槽。
 *  - 每季 2~3 本；A 位 E ≤ 当季带档、B/C 位 E ≤ 当季可达上限；一切本 T ≤ 上限（超上限的女角色当季无人能接＝浪费位）；
 *  - 可达上限：Y1-2=6 / Y3-4=7 / Y5-13=8 / Y14+=9（开局加点天花板 Lv5；2026-10-04 茄子令上抬一档——特殊剧情/特殊剧本将加快升咖）；
 *  - 第 3 槽×6 = 小成本电影 SCRIPT_156~161（Y2Q2 抢麦 / Y3Q4 42天 / Y4Q4 无此镇 / Y5Q2 一层地下 / Y5Q4 第七证人 / Y7Q3 旅馆与海），
 *    第 3 槽×2 = 丙′删尾补档（Y18Q2 对半分 / Y18Q3 永夜航路）。
 *  - 2026-10-06 茄子令：《无此镇》(SCRIPT_156) 提级为特殊剧本 SCRIPT_903 移出普通池 ⇒ 池 149 本 = 142 主槽 + 7 个第 3 槽，Y4Q4 回 2 本主槽；
 *    同日新增 SCRIPT_162《标准音》回填 Y4Q4 第 3 槽 ⇒ 池恢复 150 本（156 为弃用空号）。
 *
 * 结构：SCHEDULE[year * 4 + quarter] = 本季上架的剧本 ID 列表（2~3 部，A 位可达本在前）。
 *  - year 为 0-indexed（第 1 年 = 0），quarter 0-indexed（Q1 = 0），与 player.year / player.month 口径一致；
 *  - Y18Q4 为空（丙′删尾收官季）；
 *  - 越级钩子不再单独登记：B 位 E 落在 (带档, 上限] 区间即天然「看得见、签不到」，留给加成周目。
 *  - 2026-10-27 滚动排期：数据行不动，上架从「季粒度整单」改「月历切片推导」——行内第 i 本季内第
 *    i+1 月入池、在池 3 个月、池满次月开机（见文件尾 getScheduledScripts / poolEntryAbsOf / POOL_MONTHS）。
 */

// 全 18 年 · 72 季固定排期（整局不上架第二次；Y18Q4 收官空季）
export const SCHEDULE: string[][] = [
    // 第 1 年 Q1（22 岁）｜一九九六的雪 / 响器｜带Lv2~6
    ['SCRIPT_018', 'SCRIPT_155'],
    // 第 1 年 Q2（22 岁）｜凤座 / 时差恋人｜带Lv2~6（2026-10-06 轮换：凤座入、萌宝探班记→Y2Q1）
    ['SCRIPT_017', 'SCRIPT_027'],
    // 第 1 年 Q3（22 岁）｜毕业倒计时 / 我家老头上热搜了｜带Lv2~6
    ['SCRIPT_043', 'SCRIPT_015'],
    // 第 1 年 Q4（22 岁）｜醒木 / 凶宅试睡员｜带Lv2~6
    ['SCRIPT_062', 'SCRIPT_090'],
    // 第 2 年 Q1（23 岁）｜萌宝探班记 / 柳叶刀｜带Lv2~6（2026-10-06 轮换：萌宝入、白月光退货指南→Y3Q4）
    ['SCRIPT_016', 'SCRIPT_085'],
    // 第 2 年 Q2（23 岁）｜来路不明 / 光斑｜带Lv2~6（2026-10-20 茄子令：该季特殊剧本窗口密集，抢麦挪至 Y2Q4 第 3 槽）
    ['SCRIPT_020', 'SCRIPT_040'],
    // 第 2 年 Q3（23 岁）｜狗都知道 / 欠条｜带Lv2~6
    ['SCRIPT_024', 'SCRIPT_158'],
    // 第 2 年 Q4（23 岁）｜首席口译 / 半路人家 / 抢麦｜带Lv2~6（2026-10-20 抢麦自 Y2Q2 挪入）
    ['SCRIPT_037', 'SCRIPT_012', 'SCRIPT_160'],
    // 第 3 年 Q1（24 岁）｜借脸 / 鹤归辞｜带Lv3~7
    ['SCRIPT_021', 'SCRIPT_049'],
    // 第 3 年 Q2（24 岁）｜偏航降落 / 守夜人｜带Lv3~7（2026-10-20 乔子珊锚点：偏航降落与托举对调，落此自 Y9Q2）
    ['SCRIPT_026', 'SCRIPT_084'],
    // 第 3 年 Q3（24 岁）｜女户 / 喜帖｜带Lv3~7
    ['SCRIPT_053', 'SCRIPT_086'],
    // 第 3 年 Q4（24 岁）｜失语名单 / 42天 / 白月光退货指南｜带Lv3~7（2026-10-06 轮换：凤座→Y1Q2，白月光补入）
    ['SCRIPT_078', 'SCRIPT_045', 'SCRIPT_019'],
    // 第 4 年 Q1（25 岁）｜晚风过境 / 清场｜带Lv3~7
    ['SCRIPT_034', 'SCRIPT_159'],
    // 第 4 年 Q2（25 岁）｜打烊之前 / 清浊录｜带Lv3~7
    ['SCRIPT_025', 'SCRIPT_056'],
    // 第 4 年 Q3（25 岁）｜凤凰台 / 洼地｜带Lv3~7
    ['SCRIPT_052', 'SCRIPT_138'],
    // 第 4 年 Q4（25 岁）｜招魂笔 / 劝离 / 标准音｜带Lv3~7（原无此镇 2026-10-06 提级为特殊剧本，由标准音回填）
    ['SCRIPT_091', 'SCRIPT_023', 'SCRIPT_162'],
    // 第 5 年 Q1（26 岁）｜心证 / 显影｜带Lv4~8
    ['SCRIPT_149', 'SCRIPT_082'],
    // 第 5 年 Q2（26 岁）｜引航 / 暗账 / 一层地下｜带Lv4~8
    ['SCRIPT_092', 'SCRIPT_080', 'SCRIPT_157'],
    // 第 5 年 Q3（26 岁）｜追尾灯 / 雾岛来信｜带Lv4~8
    ['SCRIPT_074', 'SCRIPT_102'],
    // 第 5 年 Q4（26 岁）｜酒过三巡 / 进京三阶 / 第七证人｜带Lv4~8
    ['SCRIPT_104', 'SCRIPT_051', 'SCRIPT_148'],
    // 第 6 年 Q1（27 岁）｜门牌 / 满血复活｜带Lv4~8
    ['SCRIPT_154', 'SCRIPT_048'],
    // 第 6 年 Q2（27 岁）｜沉香引 / 高定女孩｜带Lv4~8
    ['SCRIPT_059', 'SCRIPT_088'],
    // 第 6 年 Q3（27 岁）｜千秋策 / 一年到期，概不退换｜带Lv4~8
    ['SCRIPT_055', 'SCRIPT_029'],
    // 第 6 年 Q4（27 岁）｜金牌法务 / 天枢陷落｜带Lv4~8
    ['SCRIPT_038', 'SCRIPT_109'],
    // 第 7 年 Q1（28 岁）｜散伙饭 / 热搜救火队｜带Lv4~8
    ['SCRIPT_100', 'SCRIPT_039'],
    // 第 7 年 Q2（28 岁）｜凤归朝 / 过闸｜带Lv4~8
    ['SCRIPT_061', 'SCRIPT_095'],
    // 第 7 年 Q3（28 岁）｜老兵不装了 / 折星 / 旅馆与海｜带Lv4~8
    ['SCRIPT_014', 'SCRIPT_068', 'SCRIPT_161'],
    // 第 7 年 Q4（28 岁）｜晨钟 / 剑冢｜带Lv4~8
    ['SCRIPT_081', 'SCRIPT_099'],
    // 第 8 年 Q1（29 岁）｜玉关辞 / 半城玉｜带Lv5~8
    ['SCRIPT_103', 'SCRIPT_050'],
    // 第 8 年 Q2（29 岁）｜急诊室白月光 / 晚安曲｜带Lv5~8
    ['SCRIPT_030', 'SCRIPT_022'],
    // 第 8 年 Q3（29 岁）｜销户 / 珠帘卷｜带Lv5~8
    ['SCRIPT_077', 'SCRIPT_054'],
    // 第 8 年 Q4（29 岁）｜恩爱夫妻 / 握手楼｜带Lv5~8
    ['SCRIPT_131', 'SCRIPT_152'],
    // 第 9 年 Q1（30 岁）｜问花不语 / 夜航七客｜带Lv5~8
    ['SCRIPT_067', 'SCRIPT_073'],
    // 第 9 年 Q2（30 岁）｜托举 / 拖网｜带Lv5~8（2026-10-20 乔子珊锚点：托举与偏航降落对调，落此自 Y3Q2）
    ['SCRIPT_101', 'SCRIPT_146'],
    // 第 9 年 Q3（30 岁）｜慢自行车 / 拆手｜带Lv5~8
    ['SCRIPT_042', 'SCRIPT_096'],
    // 第 9 年 Q4（30 岁）｜金丝笼 / 她的房间｜带Lv5~8
    ['SCRIPT_013', 'SCRIPT_032'],
    // 第 10 年 Q1（31 岁）｜选修课 / 离岸三十海里｜带Lv5~8
    ['SCRIPT_041', 'SCRIPT_035'],
    // 第 10 年 Q2（31 岁）｜灯下黑 / 潜渊｜带Lv5~8
    ['SCRIPT_147', 'SCRIPT_126'],
    // 第 10 年 Q3（31 岁）｜棋帷 / 雪原纵深｜带Lv5~8
    ['SCRIPT_057', 'SCRIPT_142'],
    // 第 10 年 Q4（31 岁）｜首付 / 收灯人｜带Lv5~8
    ['SCRIPT_044', 'SCRIPT_124'],
    // 第 11 年 Q1（32 岁）｜厉总的专属小甜妻 / 折桂令｜带Lv6~8
    ['SCRIPT_028', 'SCRIPT_060'],
    // 第 11 年 Q2（32 岁）｜君心 / 疏川记｜带Lv6~8
    ['SCRIPT_058', 'SCRIPT_105'],
    // 第 11 年 Q3（32 岁）｜门诊部的灯 / 一剑霜寒｜带Lv6~8
    ['SCRIPT_144', 'SCRIPT_106'],
    // 第 11 年 Q4（32 岁）｜迷雾申江 / 断蛟渡｜带Lv6~8
    ['SCRIPT_083', 'SCRIPT_072'],
    // 第 12 年 Q1（33 岁）｜耳听为实 / 雪围城｜带Lv6~8
    ['SCRIPT_079', 'SCRIPT_064'],
    // 第 12 年 Q2（33 岁）｜风暴眼 / 青绿｜带Lv6~8
    ['SCRIPT_094', 'SCRIPT_118'],
    // 第 12 年 Q3（33 岁）｜尖兵出鞘 / 琴房｜带Lv6~8
    ['SCRIPT_139', 'SCRIPT_119'],
    // 第 12 年 Q4（33 岁）｜余火 / 鸣沙引｜带Lv6~8
    ['SCRIPT_151', 'SCRIPT_107'],
    // 第 13 年 Q1（34 岁）｜烽堠狼烟 / 寰宇纪元｜带Lv6~8
    ['SCRIPT_063', 'SCRIPT_113'],
    // 第 13 年 Q2（34 岁）｜九霄阙 / 三餐四季｜带Lv6~8
    ['SCRIPT_127', 'SCRIPT_033'],
    // 第 13 年 Q3（34 岁）｜武魁 / 夜行长安｜带Lv6~8
    ['SCRIPT_065', 'SCRIPT_071'],
    // 第 13 年 Q4（34 岁）｜收网 / 天庭考核办｜带Lv6~8
    ['SCRIPT_075', 'SCRIPT_108'],
    // 第 14 年 Q1（35 岁）｜顶岗 / 宝船｜带Lv7~9
    ['SCRIPT_153', 'SCRIPT_112'],
    // 第 14 年 Q2（35 岁）｜归零 / 青崖小妖传｜带Lv7~9
    ['SCRIPT_046', 'SCRIPT_069'],
    // 第 14 年 Q3（35 岁）｜不打烊 / 司天监｜带Lv7~9
    ['SCRIPT_120', 'SCRIPT_125'],
    // 第 14 年 Q4（35 岁）｜潮起时 / 合租许可证｜带Lv7~9
    ['SCRIPT_133', 'SCRIPT_031'],
    // 第 15 年 Q1（36 岁）｜送灯 / 长线｜带Lv7~9
    ['SCRIPT_089', 'SCRIPT_137'],
    // 第 15 年 Q2（36 岁）｜长梦 / 衔石记｜带Lv7~9
    ['SCRIPT_122', 'SCRIPT_116'],
    // 第 15 年 Q3（36 岁）｜缚生咒 / 军功章｜带Lv7~9
    ['SCRIPT_066', 'SCRIPT_143'],
    // 第 15 年 Q4（36 岁）｜塔兰迪姆 / 地宫｜带Lv7~9
    ['SCRIPT_135', 'SCRIPT_128'],
    // 第 16 年 Q1（37 岁）｜护城河底 / 晚点落地｜带Lv8~9
    ['SCRIPT_076', 'SCRIPT_087'],
    // 第 16 年 Q2（37 岁）｜第十年特刊 / 地锚｜带Lv8~9
    ['SCRIPT_036', 'SCRIPT_093'],
    // 第 16 年 Q3（37 岁）｜昆仑阙 / 水磨调｜带Lv8~9
    ['SCRIPT_132', 'SCRIPT_110'],
    // 第 16 年 Q4（37 岁）｜稳赚不赔 / 镖不喊沧｜带Lv8~9
    ['SCRIPT_129', 'SCRIPT_097'],
    // 第 17 年 Q1（38 岁）｜江桥 / 三线家属院｜带Lv8~9
    ['SCRIPT_141', 'SCRIPT_145'],
    // 第 17 年 Q2（38 岁）｜北壁 / 西南小姐｜带Lv8~9
    ['SCRIPT_117', 'SCRIPT_130'],
    // 第 17 年 Q3（38 岁）｜深空回声 / 遇雪行｜带Lv8~9
    ['SCRIPT_111', 'SCRIPT_070'],
    // 第 17 年 Q4（38 岁）｜焚稿 / 无声站台｜带Lv8~9
    ['SCRIPT_121', 'SCRIPT_150'],
    // 第 18 年 Q1（39 岁）｜无声地带 / 拉格朗日｜带Lv9~9
    ['SCRIPT_136', 'SCRIPT_114'],
    // 第 18 年 Q2（39 岁）｜听证 / 山海归墟 / 对半分｜带Lv9~9（末位对半分=丙′删尾补档，原 Y18Q4）
    ['SCRIPT_115', 'SCRIPT_123', 'SCRIPT_047'],
    // 第 18 年 Q3（39 岁）｜首渡 / 走镖 / 永夜航路｜带Lv9~9（末位永夜航路=丙′删尾补档，原 Y18Q4）
    ['SCRIPT_140', 'SCRIPT_098', 'SCRIPT_134'],
    // 第 18 年 Q4（39 岁）·收官季（丙′删尾，2026-10-01 拍板）：不上新片单——收角于 Q3 的批是最后的制作，
    // 214-216 月拍完杀青全落生涯内；若保留本季片单，收角在 216 月末、制作落 217-219 月=退役之后（幽灵合同）。
    [],
];

/**
 * 获取「该月新入池」的剧本列表（滚动排期 2026-10-27 改版：旧语义=本季整单上架，新语义=季单的月历切片）。
 * 行内第 i 本在季内第 i+1 个月入池（2 本季占前两个月、3 本季月月有新剧）；在池 POOL_MONTHS 个月
 * （可试镜/直签），池满次月开机（收口夜=池满月月末）。数据行本体与注释不动，月历是推导层——
 * docs/剧本固定排期-18年.md 与 _check_schedule_sync 哨兵照旧对账。
 * 超出 18 年（如第 19 年收官期的评奖月）返回空数组。
 */
export function getScheduledScripts(year: number, month: number): string[] {
    const quarter = Math.floor((month - 1) / 3);
    const idx = year * 4 + quarter;
    const row = SCHEDULE[idx] ?? [];
    if (idx === LAST_SCRIPTED_IDX) {
        // 收官特例：全行季首月同批入池，保住丙′删尾结论「制作全落退役月前」——逐月铺开会让
        // 末位开拍 216 月的剧拍成幽灵合同（与 Y18Q4 空季同一理由）
        return (month - 1) % 3 === 0 ? [...row] : [];
    }
    const entry = row[(month - 1) % 3];
    return entry ? [entry] : [];
}

/** 在池月数（2026-10-27 滚动排期拍板）：入池后可试镜/直签的月数，池满次月开机 */
export const POOL_MONTHS = 3;

/** 收官特例行下标：SCHEDULE 最后一个非空行（Y18Q3）。找不到=-1（表全空，测试环境兜底） */
const LAST_SCRIPTED_IDX = (() => {
    for (let i = SCHEDULE.length - 1; i >= 0; i--) {
        if ((SCHEDULE[i] ?? []).length > 0) return i;
    }
    return -1;
})();

/**
 * 剧本入池绝对月（year*12+month 口径）：行内第 i 本 = 季首月 + i；收官特例行整行=季首月。
 * 特殊剧/新手专属剧不进排期表 ⇒ null（调用方各自走定角夜/签约次月口径）。
 */
export function poolEntryAbsOf(scriptId: string): number | null {
    for (let idx = 0; idx < SCHEDULE.length; idx++) {
        const i = (SCHEDULE[idx] ?? []).indexOf(scriptId);
        if (i < 0) continue;
        const year = Math.floor(idx / 4);
        const quarter = idx % 4;
        const quarterStart = year * 12 + quarter * 3 + 1;
        return idx === LAST_SCRIPTED_IDX ? quarterStart : quarterStart + i;
    }
    return null;
}
